import cc, { CurrencyCodeRecord } from "currency-codes";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { numberFormat } from "../../helpers/intl";
import { palette } from "../../styles/constants";
import { match } from "../../util/match";
import { P } from "../Typography";
import View from "../View";

type MoneyProps = {
    amount: number | undefined;
    currency?: string;
    boldCurrency?: boolean;
    boldCurrencySameSize?: boolean;
    hideCurrency?: boolean;
    doubleRow?: boolean;
    alignRight?: boolean;
    currencyBefore?: boolean;
    abbreviate?: boolean;
    options?: Intl.NumberFormatOptions;
    hideTitle?: boolean;
};

export const abbreviateAmount = (
    amount: number,
    exponent: number,
    language: string | undefined,
    options?: Intl.NumberFormatOptions,
) => {
    const normalCurrencyAmount = amount / Math.pow(10, exponent);
    try {
        if (normalCurrencyAmount >= 1e12) {
            return numberFormat(language, {
                notation: "compact",
                compactDisplay: "short",
                maximumFractionDigits: exponent,
                ...options,
            }).format(normalCurrencyAmount);
        }
        if (normalCurrencyAmount >= 1e9) {
            return numberFormat(language, {
                notation: "compact",
                compactDisplay: "short",
                maximumFractionDigits: exponent,
                ...options,
            }).format(normalCurrencyAmount);
        }
        if (normalCurrencyAmount >= 1e6) {
            return numberFormat(language, {
                notation: "compact",
                compactDisplay: "short",
                maximumFractionDigits: exponent,
                ...options,
            }).format(normalCurrencyAmount);
        }
        if (normalCurrencyAmount >= 1) {
            return numberFormat(language, {
                maximumFractionDigits: 0,
                ...options,
            }).format(normalCurrencyAmount);
        }
        return numberFormat(language, {
            maximumFractionDigits: exponent,
            ...options,
        }).format(normalCurrencyAmount);
    } catch {
        /** Mitigation for Safari 14.1 and lower when using newer NumberFormat spec */
        const safeOptions = options
            ? Object.fromEntries(
                  Object.entries(options).filter(
                      ([key]) => !["notation", "compactDisplay"].includes(key),
                  ),
              )
            : {};
        return numberFormat(language, {
            maximumFractionDigits: exponent,
            ...safeOptions,
        }).format(normalCurrencyAmount);
    }
};

const Money = ({
    currency,
    amount,
    boldCurrency,
    boldCurrencySameSize,
    hideCurrency,
    doubleRow,
    alignRight,
    currencyBefore,
    options,
    hideTitle = false,
    abbreviate = false,
}: MoneyProps) => {
    const { i18n } = useTranslation();
    const exponent =
        currency && cc.code(currency)
            ? (cc.code(currency) as CurrencyCodeRecord).digits
            : 2;
    const normalCurrencyAmount = (amount || 0) / Math.pow(10, exponent);
    const amountString = match(
        abbreviate,
        [
            true,
            () =>
                abbreviateAmount(amount || 0, exponent, i18n.language, options),
        ],
        [
            false,
            () =>
                amount === undefined
                    ? "?"
                    : numberFormat(i18n.language, {
                          minimumFractionDigits: exponent,
                          ...options,
                      }).format(normalCurrencyAmount),
        ],
        [
            undefined,
            () =>
                amount === undefined
                    ? "?"
                    : numberFormat(i18n.language, {
                          minimumFractionDigits: exponent,
                          ...options,
                      }).format(normalCurrencyAmount),
        ],
    );

    if (doubleRow) {
        return (
            <View
                direction="column"
                wrap="nowrap"
                alignItems={alignRight ? "flex-end" : "flex-start"}
            >
                <P
                    lineHeight={20}
                    whiteSpace="nowrap"
                    fontFeatureSettings="tnum"
                >
                    {amountString}
                </P>
                <P lineHeight={20} color={palette.neutral[500]}>
                    {currency}
                </P>
            </View>
        );
    }

    if (hideCurrency) {
        return (
            <Wrapper title={hideTitle ? undefined : amountString}>
                {amountString}
            </Wrapper>
        );
    }
    if (boldCurrencySameSize) {
        return (
            <Wrapper
                title={hideTitle ? undefined : `${amountString} ${currency}`}
            >
                {amountString} <Currency data-ignore-size>{currency}</Currency>
            </Wrapper>
        );
    }
    if (currencyBefore) {
        if (boldCurrency) {
            return (
                <Wrapper
                    title={
                        hideTitle ? undefined : `${currency} ${amountString}`
                    }
                >
                    <Currency>{currency}</Currency> {amountString}
                </Wrapper>
            );
        }
        return (
            <Wrapper
                title={hideTitle ? undefined : `${currency} ${amountString}`}
            >
                {currency} {amountString}
            </Wrapper>
        );
    }
    if (boldCurrency) {
        return (
            <Wrapper
                title={hideTitle ? undefined : `${amountString} ${currency}`}
            >
                {amountString} <Currency>{currency}</Currency>
            </Wrapper>
        );
    }
    return (
        <Wrapper title={hideTitle ? undefined : `${amountString} ${currency}`}>
            {amountString} {currency}
        </Wrapper>
    );
};

const Wrapper = styled.span`
    white-space: nowrap;
    font-feature-settings: 'tnum';
    position: relative;
`;

const Currency = styled.span`
    &[data-ignore-size="false"] {
        font-size: 11px;
    }
    font-weight: 700;
`;

export default Money;
